import { blockingAtCell, cache, cellAtWorld } from '../../grid.ts';
import { cosA, dist2, iatan2, idiv, imin, isqrt, sinA } from '../../math/fixed.ts';
import type { BattleState, Enemy, Hero, UnsleepingState } from '../../types.ts';
import { ev } from '../combat.ts';
import { blowOf } from '../kits.ts';
import { standing } from './kit.ts';

/*
 * What the Unsleeping who fight from afar share (Vela, Ansel): standing off and shooting the hero they pick, lines
 * through the heroes, telling who stood still, and leaping over free ground.
 */

/** Whether (x, y) lies on the line from (ax, ay) along (dx, dy) (`d` its length): between `from` and `to` along it, within `width` of it. */
export const onLine = (ax: number, ay: number, dx: number, dy: number, d: number, x: number, y: number, from: number, to: number, width: number): boolean => {
  if (d <= 0) return false;
  const px = x - ax;
  const py = y - ay;
  const t = idiv(px * dx + py * dy, d);
  if (t < from || t > to) return false;
  const c = px * dy - py * dx;
  return idiv(c < 0 ? -c : c, d) <= width;
};

/** Standing heroes on a line from `e` toward (tx, ty), `len` long and `width` wide. */
export const heroesOnLine = (s: BattleState, e: Enemy, tx: number, ty: number, len: number, width: number): Hero[] => {
  const dx = tx - e.x;
  const dy = ty - e.y;
  const d = isqrt(dx * dx + dy * dy);
  if (d === 0) return [];
  return s.heroes.filter((h) => standing(h) && onLine(e.x, e.y, dx, dy, d, h.x, h.y, 0, len, width));
};

/** How long each standing hero has stood still (`unStill`), while an Unsleeping who punishes it is out. */
export const trackStill = (s: BattleState): void => {
  for (const h of s.heroes) {
    if (!standing(h) || h.moving || (h.shove ?? 0) > 0) h.unStill = 0;
    else h.unStill = imin(10000, (h.unStill ?? 0) + 1);
  }
};

/** Whether a hero stood still at least `ticks`. */
export const stood = (h: Hero, ticks: number): boolean => (h.unStill ?? 0) >= ticks;

/**
 * The hero he shoots: within `range`, the one he hunts first, then one standing still at least `still` ticks, then
 * the nearest. Faced; his blow (`strike`) when it is ready. False with no hero in range (he walks on).
 */
export const shoot = (s: BattleState, e: Enemy, u: UnsleepingState, range: number, still: number, kind: string, strike: (s: BattleState, e: Enemy, h: Hero, dmg: number) => void): boolean => {
  let best: Hero | null = null;
  let bs = 0;
  for (const h of s.heroes) {
    if (!standing(h)) continue;
    const d = dist2(e.x, e.y, h.x, h.y);
    if (d > range * range) continue;
    // Hunted first, then the still, then the nearest (the squared distance stays under 2^36).
    const score = (h.pid === u.hunt ? 0 : 2) * 0x1000000000 + (stood(h, still) ? 0 : 1) * 0x1000000000 + d;
    if (!best || score < bs) {
      best = h;
      bs = score;
    }
  }
  if (!best) return false;
  e.facing = iatan2(best.y - e.y, best.x - e.x);
  e.tgtKind = 1;
  e.tgtId = best.id;
  if (e.cd > 0) return true;
  e.cd = e.reload;
  e.anim = 'attack';
  e.animT = 0;
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: best.x, ty: best.y, kind });
  strike(s, e, best, blowOf(s, e, e.damage));
  return true;
};

/** Ground he can stand on and pass over: on the map, reached by the flow, nothing built on it. */
export const freeAt = (s: BattleState, x: number, y: number): boolean => {
  const c = cellAtWorld(s, x, y);
  if (c < 0 || cache(s).flow[c] === -1) return false;
  const b = blockingAtCell(s, c);
  return !b || b.destroyed;
};

/**
 * A leap away from (fx, fy), `far` long in steps of `step`: straight away if the ground lets it, else turned a little
 * (45°, then 90°) to the side with the longest free run. Sets his `busy`, `vx`, `vy`; false when no way is free.
 */
export const leapAway = (s: BattleState, e: Enemy, u: UnsleepingState, fx: number, fy: number, far: number, step: number): boolean => {
  const base = fx === e.x && fy === e.y ? e.facing + 512 : iatan2(e.y - fy, e.x - fx);
  const steps = idiv(far + step - 1, step);
  let best = 0;
  let ba = base;
  for (const off of [0, 128, -128, 256, -256]) {
    const a = base + off;
    const vx = idiv(step * cosA(a), 10000);
    const vy = idiv(step * sinA(a), 10000);
    let n = 0;
    while (n < steps && freeAt(s, e.x + vx * (n + 1), e.y + vy * (n + 1))) n++;
    if (n > best) {
      best = n;
      ba = a;
    }
    if (n === steps) break;
  }
  if (best === 0) return false;
  u.busy = best;
  u.vx = idiv(step * cosA(ba), 10000);
  u.vy = idiv(step * sinA(ba), 10000);
  return true;
};
